import { FC, useMemo } from 'react'
import { isPreviousMonth } from '@/core/utils/isPreviousMonth.ts'
import { getPlanFormattedDateFromDate } from '@/core/utils/getPlanFormattedDateFromDate.ts'
import { Amount, Cost, Wrapper } from './PlanTableMonthFooter.styles'
import { formatNumber } from '@/core/utils/formatNumber.ts'
import { formatCurrency } from '@/core/utils/formatCurrency.ts'
import { useSelector } from 'react-redux'
import {
  selectFilteredSortedPlanProducts,
  selectForecastItems,
  selectPlannedItems,
  selectShipmentItems,
} from '@/data/replenishmentPlan/slice/replenishmentPlan.selectors.ts'
import {
  shouldHighlightMonth,
  isCurrentMonth,
} from '@/core/utils/shouldHighlightMonth.ts'

interface Props {
  date: Date
}

const PlanTableMonthFooter: FC<Props> = ({ date }) => {
  const forecastItems = useSelector(selectForecastItems)
  const plannedItems = useSelector(selectPlannedItems)
  const shipmentItems = useSelector(selectShipmentItems)

  const products = useSelector(selectFilteredSortedPlanProducts)

  const shouldHighlight = shouldHighlightMonth(date)
  const isCurrent = isCurrentMonth(date)

  const { amount, cost } = useMemo(() => {
    const isPrevious = isPreviousMonth(date)
    const planFormattedDate = getPlanFormattedDateFromDate(date)

    let amount = 0
    let cost = 0

    if (isPrevious) {
      for (const product of products) {
        const productShipmentItems = shipmentItems[product.id]

        if (!productShipmentItems) {
          continue
        }

        const monthShipments = productShipmentItems[planFormattedDate]

        if (monthShipments) {
          amount += monthShipments.reduce((acc, item) => acc + item.amount, 0)
          cost += monthShipments.reduce(
            (acc, item) => acc + item.amount * item.price,
            0,
          )
        }
      }
    } else {
      for (const product of products) {
        const productPlanItems = plannedItems[product.id]
        const productForecastItems = forecastItems[product.id]

        if (!productPlanItems && !productForecastItems) {
          continue
        }

        const productMonthPlanItem = productPlanItems
          ? productPlanItems[planFormattedDate]
          : null
        const productMonthForecastItem = productForecastItems
          ? productForecastItems[planFormattedDate]
          : null

        if (productMonthPlanItem) {
          amount += productMonthPlanItem.amount
          cost += productMonthPlanItem.amount * product.price
        } else if (productMonthForecastItem) {
          amount += productMonthForecastItem.amountSuggested
          cost += productMonthForecastItem.amountSuggested * product.price
        }
      }
    }

    return { amount, cost }
  }, [date, forecastItems, plannedItems, products, shipmentItems])

  return (
    <Wrapper className={shouldHighlight ? 'highlight' : ''}>
      <Amount sx={{ fontWeight: isCurrent ? 700 : 400 }}>
        {formatNumber(amount)}
      </Amount>
      <Cost sx={{ fontWeight: isCurrent ? 700 : 400 }}>
        {formatCurrency(cost, { maximumFractionDigits: 0 })}
      </Cost>
    </Wrapper>
  )
}

export default PlanTableMonthFooter
